import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { Callout } from '@/components/notifications/Callout/Callout';
import { pluralize } from '@/utils/formatting/strings';

interface OppositeDeathOrderSuggestionsCalloutProps {
  /** Number of unacknowledged suggestions in the opposite death order */
  suggestionsCount: number;
  /** Display name of the client who dies first in the opposite death order */
  clientName: string;
  /** Whether the opposite death order is the "primary" (first client dies first) */
  isPrimaryDeathOrder: boolean;
  /** Callback when the user clicks "Switch death order" */
  onSwitchDeathOrder: () => void;
}

/**
 * Callout shown when the current death order has no suggestions but the
 * opposite death order does have suggestions.
 */
export function OppositeDeathOrderSuggestionsCallout({
  suggestionsCount,
  clientName,
  isPrimaryDeathOrder,
  onSwitchDeathOrder,
}: OppositeDeathOrderSuggestionsCalloutProps) {
  const deathOrderLabel = isPrimaryDeathOrder
    ? 'primary death order'
    : 'secondary death order';

  return (
    <Callout severity="success" skipTextWrapper>
      <Stack width="100%" spacing={0.5} alignItems="flex-start">
        <Typography variant="title-xs">
          {pluralize({
            count: suggestionsCount,
            singular: 'suggestion',
          })}{' '}
          available
        </Typography>
        <Typography variant="body-xs">
          The {clientName} dies first ({deathOrderLabel}) has suggestions
        </Typography>
        <Box pt={0.5}>
          <Button
            variant="primary"
            size="xs"
            onClick={onSwitchDeathOrder}
            data-testid="switch-death-order-button"
          >
            Switch death order
          </Button>
        </Box>
      </Stack>
    </Callout>
  );
}
